import { API_SCOPE, msalInstance } from './msalConfig';

/**
 * Acquires an access token for the API scope and returns it as an Authorization
 * header. Falls back to an interactive redirect when silent acquisition fails
 * (expired token, consent required, no cached account).
 *
 * Returns null when a redirect has been kicked off, in which case the caller
 * should stop — the page is about to navigate away.
 */
export async function getAuthHeader(): Promise<Record<string, string> | null> {
  const account = msalInstance.getAllAccounts()[0];

  if (!account) {
    await msalInstance.loginRedirect({ scopes: ['openid', 'profile', API_SCOPE] });
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account
    });
    // The access token, not the ID token. An ID token's audience will not validate.
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch {
    await msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE], account });
    return null;
  }
}

/**
 * Use this for every call to a protected endpoint. Centralising it is the point:
 * it removes the chance of a bare fetch() slipping through unauthenticated.
 *
 * Safe with FormData bodies — Content-Type is never set here, so the browser
 * keeps control of the multipart boundary.
 */
export async function authFetch(url: string, options?: RequestInit): Promise<Response> {
  const authHeader = await getAuthHeader();

  if (!authHeader) {
    throw new Error('Redirecting for sign-in…');
  }

  const opts: RequestInit = { ...(options || {}) };
  opts.headers = { ...(opts.headers as Record<string, string> | undefined), ...authHeader };

  return fetch(url, opts);
}

/**
 * Fetches a protected PDF as a blob and opens it.
 *
 * A plain window.open('/docs/x.pdf') cannot carry an Authorization header, so
 * the file is retrieved with a bearer token first and then handed to the browser
 * as an object URL. Tries a new tab; if the popup is blocked, navigates the
 * current tab instead so the user still gets the document.
 */
export async function openProtectedPdf(fileName: string): Promise<void> {
  const response = await authFetch(`/docs/${encodeURIComponent(fileName)}`);

  if (!response.ok) {
    throw new Error(`Could not open ${fileName} (${response.status})`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  // Open via an anchor element with rel="noopener noreferrer" instead of
  // window.open(url, '_blank', 'noopener'). Passing "noopener" to window.open
  // makes it return null even on success, which previously tripped the popup
  // fallback and navigated the current tab too (opening the PDF twice).
  // The anchor approach keeps the tabnabbing/referrer protection while letting
  // us detect a genuinely blocked popup.
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Release the object URL once the new tab has had time to load it.
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
